import { Body2, Button, ButtonType, Subtitle1, useAlert, userContext, useTheme } from "capsule"
import React, { useCallback, useContext, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Platform, StyleSheet, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

import { functions } from "../../capsule/features/Firebase/firebase"
import { DeleteAccountNS, NavigationNS } from "../../features/i18n/constants"
import { maxWidth } from "../../ThemeApp"

const DeleteAccountScreen = () => {
  const { userDocRef, logout, user, userData } = useContext(userContext)
  const { t } = useTranslation()
  const { showDialog } = useAlert()
  const [isDeleting, setIsDeleting] = useState(false)
  const isPatient = userData?.role === "patient"

  const {
    colors: {
      black,
      surface: { background },
    },
    dimensions: { spacing },
    fontMaker,
  } = useTheme()

  const s = useMemo(
    () => ({
      safe: [
        styles.safe,
        {
          paddingLeft: spacing * 2,
          paddingRight: spacing * 2,
          backgroundColor: background,
        },
      ],
      paragraph: {
        paddingBottom: spacing,
      },
      bold: fontMaker({ weight: "Bold" }),
      dialogTextColor: {
        color: black.mediumEmphasis,
      },
      screenStyle: [
        Platform.OS === "web" ? { width: maxWidth } : null,
        { paddingTop: 0, backgroundColor: background },
      ],
      loadingText: {
        marginTop: spacing,
        textAlign: "center" as const,
      },
    }),
    [spacing, fontMaker, black.mediumEmphasis, background],
  )

  const deleteAccount = useCallback(async () => {
    if (isDeleting) {
      return
    }
    setIsDeleting(true)

    showDialog({
      type: "button",
      dismissable: false,
      messageStyle: s.dialogTextColor,
      contentContainerStyle: styles.loadingDialog,
      title: t(
        isPatient
          ? `${DeleteAccountNS}:deletingTitle`
          : `${DeleteAccountNS}:practitionerDeletingTitle`,
      ),
      message: t(
        isPatient
          ? `${DeleteAccountNS}:deletingMessage`
          : `${DeleteAccountNS}:practitionerDeletingMessage`,
      ),
      positive: {
        label: " ",
        onPress: () => {},
      },
    })

    try {
      if (isPatient) {
        // PATIENT: Direct anonymization
        const anonymizePatient = functions().httpsCallable("anonymizePatient")
        await anonymizePatient({ patientId: user?.uid })

        await logout()
      } else {
        // HEALTHCARE PROFESSIONAL: Manual deletion by team
        await userDocRef?.set(
          {
            disabled: true,
            dateOfDeletionRequest: new Date().toISOString(),
          },
          { merge: true },
        )

        await logout()
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Error deleting account:", error)
      setIsDeleting(false)
      showDialog({
        type: "button",
        title: t(`${DeleteAccountNS}:errorTitle`),
        message: t(`${DeleteAccountNS}:errorMessage`),
        positive: {
          label: "OK",
          onPress: () => {},
        },
      })
    }
  }, [isDeleting, userDocRef, user?.uid, logout, showDialog, isPatient, t, s.dialogTextColor])

  const onPress = useCallback(async () => {
    const dialogTitle = isPatient
      ? t(`${NavigationNS}:DeleteAccount:definitiveDeletionTitle`)
      : t(`${DeleteAccountNS}:practitionerDeletionTitle`)

    const dialogMessage = isPatient
      ? t(`${NavigationNS}:DeleteAccount:definitiveDeletionParagraph`)
      : t(`${DeleteAccountNS}:practitionerDeletionParagraph`)

    const result = await showDialog({
      type: "button",
      title: dialogTitle,
      message: dialogMessage,
      messageStyle: s.dialogTextColor,
      positive: {
        buttonStyle: styles.dialogRightButton,
        type: ButtonType.POSITIVE,
        label: t(`${NavigationNS}:DeleteAccount:title`),
        labelType: "destructive",
      },
      negative: {
        buttonStyle: styles.dialogLeftButton,
        label: t(`${NavigationNS}:DeleteAccount:cancel`),
        type: ButtonType.NEGATIVE,
        textStyle: s.dialogTextColor,
      },
    })

    if (result?.button === ButtonType.POSITIVE) {
      deleteAccount()
    }
  }, [deleteAccount, t, showDialog, s.dialogTextColor, isPatient])

  return (
    <SafeAreaView style={s.safe}>
      <View style={s.screenStyle}>
        <View>
          <Subtitle1 style={s.paragraph}>
            {t(isPatient ? `${DeleteAccountNS}:title` : `${DeleteAccountNS}:practitionerTitle`)}
          </Subtitle1>
          <Body2 style={s.paragraph}>
            <Trans
              i18nKey={
                isPatient
                  ? `${DeleteAccountNS}:warningParagraph`
                  : `${DeleteAccountNS}:practitionerWarningParagraph`
              }
              // @ts-ignore
              components={{ p: <Body2 style={s.padding} />, b: <Body2 style={s.bold} /> }}
            />
          </Body2>
          <Body2 style={s.paragraph}>
            <Trans
              i18nKey={
                isPatient
                  ? `${DeleteAccountNS}:validateDeletion`
                  : `${DeleteAccountNS}:practitionerValidateDeletion`
              }
              // @ts-ignore
              components={{ p: <Body2 style={s.padding} />, b: <Body2 style={s.bold} /> }}
            />
          </Body2>
        </View>
        <View style={styles.container}>
          <Button onPress={onPress} disabled={isDeleting}>
            {t("common:button.delete")}
          </Button>
        </View>
      </View>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  safe: {
    alignItems: "center",
    flex: 1,
  },
  container: {
    width: "100%",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "space-evenly",
    paddingTop: 20,
  },
  loadingContainer: {
    width: "100%",
    alignItems: "center",
    paddingTop: 20,
  },
  dialogLeftButton: {
    marginRight: 0,
    flex: 0,
  },
  dialogRightButton: {
    marginLeft: 0,
    flex: 0,
  },
  loadingDialog: {
    width: "85%",
  },
})

export default DeleteAccountScreen
